import { useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { Action, Async, Badge, Column, FieldType, FieldValue, FilterField2, FiltersOptions, ModalId, O, option, PanelWrapper, UIPermission, useUIActions } from "../../../../../features/ui"
import { RoutePath, useRoutesHooks } from "../../../../../features/routes"
import { Data2, DataTypeList3, Labels, useDescribeHooks } from "../../../../../features/data-types"
import { DataType, TableName } from "../../../../../tables"
import { Params } from "../../../../../state/reducers/types"
import { useManagerHooks } from "../../../../manager"
import { operationHelpers, useOperationLoaders } from "../../../../operations"
import { useSystemStateHooks } from "../../../../system"
import { deliveryHelpers, useDeliveryHooks } from "../../../hooks/deliveryHooks"
import { DeliveryNoteStatusFilter } from "../../../hooks/types"
import { useDeliveryLoaders } from "../../../loaders/deliveryLoaders"
import { useDeliveryOrderLoaders } from "../../../loaders/deliveryOrderLoaders"
import { Delivery, DeliveryMethod, DeliveryOrder, DeliveryStatus } from "../../../state/types"
import { DownloadDeliveryNoteModal } from "../../delivery/download-delivery-note-modal/downloadDeliveryNoteModal"

// Shipment, pickup and return delivery notes of the current branch (receptions have their own list)
export const DeliveryNoteList = () => {
    const [currentDelivery, setCurrentDelivery] = useState<O<Delivery>>()
    const [currentDeliveryOrder, setCurrentDeliveryOrder] = useState<O<DeliveryOrder>>()

    const { describeDeliveryCode, isCompletable, isAnnulable, isReturnable } = deliveryHelpers
    const { loadDeliveryNotesBatch, loadDeliveryNotesBatchData } = useDeliveryHooks()
    const { userHasPermission } = useSystemStateHooks()
    const readOnly = !userHasPermission(UIPermission.SAVE_SALE_DELIVERIES)
    const { currentBranchId } = useManagerHooks()
    const { describe } = useDescribeHooks()
    const { getRoutePath } = useRoutesHooks()
    const navigate = useNavigate()

    const deliveryLoaders = useDeliveryLoaders()
    const deliveryOrderLoaders = useDeliveryOrderLoaders()
    const operationLoaders = useOperationLoaders()

    const { openModal } = useUIActions()

    const allOptions = 'ALL'
    const tableOptions = useMemo(() => [
        option(allOptions, "Todas"),
        option(TableName.SALE_ORDERS, "Ventas"),
        option(TableName.HOARD_ORDERS, "Acopios")
    ], [])

    const filterFields: FilterField2[] = useMemo(() => [
        {
            name: 'method',
            type: FieldType.SELECT,
            label: 'Origen',
            options: [DeliveryMethod.SHIPMENT, DeliveryMethod.PICKUP, DeliveryMethod.RETURN]
                .map(method => option(method, Labels.deliveryMethodLabel(method))),
            space: 2
        },
        {
            name: 'table',
            type: FieldType.SELECT,
            label: 'Tipo de Operación',
            options: tableOptions,
            space: 2
        },
        {
            name: 'customerId',
            type: FieldType.TABLE,
            table: TableName.CUSTOMERS,
            label: 'Cliente',
            space: 4
        },
        {
            name: 'status',
            type: FieldType.SELECT,
            label: 'Estado de Entrega',
            options: [
                option(DeliveryNoteStatusFilter.PENDING, Labels.deliveryStatusLabel(DeliveryStatus.PENDING)),
                option(DeliveryNoteStatusFilter.DELIVERED, Labels.deliveryStatusLabel(DeliveryStatus.DELIVERED)),
                option(DeliveryNoteStatusFilter.CANCELLED, 'Anulados')
            ],
            space: 4
        },
        {
            name: 'dateFrom',
            type: FieldType.DATE,
            label: 'Fecha: Desde',
            space: 2
        },
        {
            name: 'dateTo',
            type: FieldType.DATE,
            label: 'Fecha: Hasta',
            space: 2
        }
    ], [tableOptions])

    const filtersOptions: FiltersOptions = useMemo(() => ({
        filtersKey: 'delivery-notes',
        search: {
            placeholder: 'Buscar por número de remito'
        },
        filterFields,
        filtersTextAffix: currentBranchId,
        firstRowLength: 4
    }), [filterFields, currentBranchId])

    const loadDeliveryData = async (deliveryId: string) => {
        const delivery = (await deliveryLoaders().loadDelivery(deliveryId))!
        const deliveryOrder = delivery.orderId ? await deliveryOrderLoaders().loadDeliveryOrder(delivery.orderId) : undefined
        return { delivery, deliveryOrder }
    }

    const columns: Column[] = [
        {
            name: 'code',
            type: FieldType.TEXT,
            label: '#',
            render: (value?: FieldValue) => <Async
                loadFn={async () => {
                    const { delivery, deliveryOrder } = await loadDeliveryData(value! as string)
                    return <>{describeDeliveryCode(delivery, deliveryOrder)}</>
                }}
            />
        },
        {
            name: 'method',
            type: FieldType.TEXT,
            label: 'Origen',
            render: (value?: FieldValue) => Labels.deliveryMethodLabel(value! as DeliveryMethod)
        },
        {
            name: 'operationId',
            type: FieldType.TEXT,
            label: 'Operación',
            render: (value?: FieldValue) => <Async
                loadFn={async () => {
                    const { delivery } = await loadDeliveryData(value! as string)
                    const operation = await operationLoaders().loadOperation(delivery.table, delivery.operationId)
                    return <>{describe(delivery.table, operation!)}</>
                }}
            />
        },
        {
            name: 'date',
            type: FieldType.DATE,
            label: 'Fecha'
        },
        {
            name: 'customerId',
            type: FieldType.TABLE,
            table: TableName.CUSTOMERS,
            label: 'Cliente'
        },
        {
            name: 'deliveryStatus',
            type: FieldType.TEXT,
            label: 'Estado de Entrega',
            render: (value?: FieldValue) => Labels.deliveryStatusLabel(value! as DeliveryStatus)
        },
        {
            name: 'cancelled',
            type: FieldType.TEXT,
            label: '',
            render: (value?: FieldValue) => value ? <Badge label="Anulado" /> : <></>
        }
    ]

    const toRow = (deliveryDT: DataType): Data2 => {
        const { items, fromNotDeliveredItems, ...data } = deliveryDT as Delivery
        return {
            ...data,
            code: deliveryDT.id,
            operationId: deliveryDT.id
        }
    }

    const onView = (id?: string) => navigate(id!)

    const onDownload = (delivery: Delivery, deliveryOrder?: DeliveryOrder) => () => {
        setCurrentDelivery(delivery)
        setCurrentDeliveryOrder(deliveryOrder)
        openModal(ModalId.DOWNLOAD_DELIVERY_NOTE)
    }

    const onComplete = (delivery: Delivery) => () => navigate(
        `${getRoutePath(RoutePath.SHIPMENT_ORDERS)}/${delivery.orderId}/${RoutePath.COMPLETE_DELIVERY}`,
        { state: { deliveryId: delivery.id } }
    )

    const onAnnul = (deliveryId: string) => () => {
        onView(deliveryId)
        setTimeout(() => {
            openModal(ModalId.ANNUL_DELIVERY)
        }, 500)
    }

    const onRegisterReturn = (delivery: Delivery) => () => navigate(
        `${getRoutePath(operationHelpers.getRoutePath(delivery.table), delivery.operationId)}/${RoutePath.REGISTER_RETURN}`
    )

    const actions = async (rowIdValue: string) => {
        const { delivery, deliveryOrder } = await loadDeliveryData(rowIdValue)
        const isReception = delivery.method === DeliveryMethod.RECEPTION

        const actionList: Action[] = [
            { icon: 'eye-fill', kind: 'tertiary', tooltip: 'Ver Detalle', onClick: onView }
        ]
        if (!isReception || delivery.deliveryNoteUrl) {
            actionList.push({ icon: 'download', kind: 'tertiary', tooltip: 'Descargar', onClick: onDownload(delivery, deliveryOrder) })
        }
        if (!readOnly) {
            if (isCompletable(delivery, deliveryOrder)) {
                actionList.push({ icon: 'bag-check-fill', tooltipLines: ['Completar', 'Envío'], onClick: onComplete(delivery) })
            }
            if (isAnnulable(delivery, deliveryOrder)) {
                actionList.push({ icon: 'slash-circle', tooltipLines: ['Anular', 'Entrega'], kind: 'secondary', onClick: onAnnul(rowIdValue) })
            }
            if (isReturnable(delivery, deliveryOrder)) {
                actionList.push({ icon: 'arrow-return-left', tooltipLines: ['Registrar', 'Devolución'], onClick: onRegisterReturn(delivery) })
            }
        }

        return actionList
    }

    const loadBatch = (
        filtersData?: Data2,
        batchSize?: number,
        startKey?: Params
    ) => {
        const updatedFiltersData = {
            ...filtersData,
            table: filtersData?.table === allOptions ? undefined : filtersData?.table
        }
        return loadDeliveryNotesBatch(updatedFiltersData, batchSize, startKey)
    }

    return (
        <PanelWrapper
            title="Remitos"
            wide
        >
            <DataTypeList3
                columns={columns}
                filtersOptions={filtersOptions}
                canSort={filtersData => !!filtersData.dateFrom || !!filtersData.customerId}
                readOnly
                toRow={toRow}
                actions={actions}
                loadBatch={loadBatch}
                loadBatchData={loadDeliveryNotesBatchData}
            />
            <DownloadDeliveryNoteModal
                delivery={currentDelivery}
                deliveryOrder={currentDeliveryOrder}
            />
        </PanelWrapper>
    )
}
